import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import LanguageSelector from './LanguageSelector';

/* Iconos de tema en SVG: los emoji ☀️/🌙 se dibujaban distinto en cada
 * sistema operativo y desentonaban con la tipografía de la barra. */
function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navbarScrolled, setNavbarScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setNavbarScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Páginas con fondo propio (sin fotografía a pantalla completa):
   * ahí la barra sigue el tema activo. El resto (inicio, contacto y el
   * 404) arrancan sobre una foto oscura, así que hasta que no aparece
   * el cristal al hacer scroll la barra va siempre en blanco: en tema
   * claro las letras negras sobre la foto no se leían. */
  const PLAIN_BG_ROUTES = ['/locales', '/faqs', '/terminos', '/proteccion-datos'];
  const hasPhotoHero = !PLAIN_BG_ROUTES.some(
    (route) => location.pathname === route || location.pathname.startsWith(`${route}/`)
  );
  const overHero = hasPhotoHero && !navbarScrolled && !menuOpen;

  const logoSrc = theme === 'light' && !overHero
    ? 'https://res.cloudinary.com/dgggdig3s/image/upload/f_auto,q_auto/BLogo_black_nobackground_u9c62d.png'
    : 'https://res.cloudinary.com/dgggdig3s/image/upload/f_auto,q_auto/BLogo_white_nobackground_ehgnnn.png';

  return (
    <header className={`navbar ${navbarScrolled ? 'navbar--scrolled' : ''} ${overHero ? 'navbar--over-hero' : ''} ${menuOpen ? 'navbar--menu-open' : ''}`}>
      <Link to="/" className="navbar-logo" aria-label="Inicio" onClick={closeMenu}>
        <img src={logoSrc} alt="Logo Brundin Gestión" />
      </Link>

      {/* Theme toggle (móvil) */}
      <button
        type="button"
        className="theme-toggle theme-toggle--mobile"
        onClick={toggleTheme}
        aria-label={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
      >
        <span className="theme-toggle-icon">
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </span>
      </button>

      <button
        type="button"
        className="navbar-burger"
        aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <span className="navbar-burger-bar" />
        <span className="navbar-burger-bar" />
        <span className="navbar-burger-bar" />
      </button>

      {/* Sin aria-hidden: en escritorio el menú siempre está visible pero
          menuOpen es false, así que marcaba la navegación principal como
          oculta para lectores de pantalla dejando dentro enlaces
          tabulables (incumple WCAG). Cerrado en móvil ya lo resuelve el
          visibility: hidden del CSS, que saca del foco y del árbol. */}
      <nav className={`navbar-menu ${menuOpen ? 'navbar-menu--open' : ''}`}>
        <div className="navbar-menu-inner">
          <Link to="/contacto" onClick={closeMenu}>{t('nav.contact')}</Link>
          <Link to="/locales" onClick={closeMenu}>{t('nav.locales')}</Link>
          <Link to="/faqs" onClick={closeMenu}>{t('nav.faqs')}</Link>
          {/* Selector dentro del menú (solo móvil) */}
          <div className="navbar-lang-mobile">
            <LanguageSelector />
          </div>
        </div>
      </nav>

      {/* Acciones agrupadas a la derecha (solo desktop): tema + idioma.
          Antes el toggle iba suelto dentro del menú centrado y parecía
          un fallo de maquetación pegado a "FAQs". */}
      <div className="navbar-actions">
        <button
          type="button"
          className="theme-toggle theme-toggle--desktop"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
        >
          <div className="theme-toggle-track">
            <span className="theme-toggle-icon-moon"><MoonIcon /></span>
            <span className="theme-toggle-icon-sun"><SunIcon /></span>
            <span className="theme-toggle-thumb" />
          </div>
        </button>
        <LanguageSelector />
      </div>
    </header>
  );
}

export default Navbar;
